import React, { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Plus, Trash2, ArrowUp, ArrowDown, Check, Pencil, X } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

// One manageable list (categories OR tags): add, inline rename, delete, reorder
const TaxonomyList = ({ kind, items, onChanged, headers, isDark }) => {
  const [newName, setNewName] = useState("");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [busy, setBusy] = useState(false);
  const base = `${API}/invoices/library/${kind}`;
  const label = kind === "categories" ? "category" : "tag";

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const add = () => {
    if (!newName.trim()) return;
    run(async () => {
      await axios.post(base, { name: newName.trim() }, { headers });
      setNewName("");
    }, `${label[0].toUpperCase() + label.slice(1)} added`);
  };

  const rename = (id) => {
    if (!editName.trim()) return;
    run(async () => {
      await axios.put(`${base}/${id}`, { name: editName.trim() }, { headers });
      setEditId(null);
    }, "Renamed");
  };

  const remove = (id) => run(() => axios.delete(`${base}/${id}`, { headers }), "Deleted");

  const move = (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    run(() => axios.put(`${base}/reorder`, { order: next.map((i) => i.id) }, { headers }));
  };

  return (
    <div className="space-y-2">
      <div className="space-y-1">
        {items.length === 0 && (
          <p className="text-xs text-muted-foreground py-2">No {kind} yet — add one below.</p>
        )}
        {items.map((it, idx) => (
          <div
            key={it.id}
            className={`flex items-center gap-1.5 rounded-lg border px-2 py-1.5 ${isDark ? "bg-white/[0.02] border-white/10" : "bg-white border-gray-200"}`}
            data-testid={`${kind}-row-${it.id}`}
          >
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} disabled={busy || idx === 0} className="text-muted-foreground hover:text-foreground disabled:opacity-25" data-testid={`${kind}-up-${it.id}`}><ArrowUp className="w-3 h-3" /></button>
              <button onClick={() => move(idx, 1)} disabled={busy || idx === items.length - 1} className="text-muted-foreground hover:text-foreground disabled:opacity-25" data-testid={`${kind}-down-${it.id}`}><ArrowDown className="w-3 h-3" /></button>
            </div>
            {editId === it.id ? (
              <>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-7 text-sm flex-1" autoFocus
                  onKeyDown={(e) => e.key === "Enter" && rename(it.id)} data-testid={`${kind}-edit-input-${it.id}`} />
                <Button variant="ghost" size="icon" className="h-7 w-7 text-green-500" onClick={() => rename(it.id)} disabled={busy} data-testid={`${kind}-edit-save-${it.id}`}><Check className="w-3.5 h-3.5" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditId(null)}><X className="w-3.5 h-3.5" /></Button>
              </>
            ) : (
              <>
                <span className="text-sm text-foreground flex-1 truncate">{it.name}</span>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" onClick={() => { setEditId(it.id); setEditName(it.name); }} data-testid={`${kind}-edit-${it.id}`}><Pencil className="w-3 h-3" /></Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-500" onClick={() => remove(it.id)} disabled={busy} data-testid={`${kind}-delete-${it.id}`}><Trash2 className="w-3 h-3" /></Button>
              </>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder={`New ${label} name…`}
          className="h-8 text-sm"
          maxLength={kind === "categories" ? 60 : 30}
          data-testid={`${kind}-new-input`}
        />
        <Button size="sm" className="h-8" onClick={add} disabled={busy || !newName.trim()} data-testid={`${kind}-add-btn`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
};

const CategoryTagManager = ({ open, onOpenChange, categories, tags, onChanged, headers, isDark }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-lg max-w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="category-tag-manager">
      <DialogHeader>
        <DialogTitle>Manage Categories & Tags</DialogTitle>
        <DialogDescription>Add, rename, reorder or delete the categories and tags used by your saved items.</DialogDescription>
      </DialogHeader>
      <div className="space-y-5">
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Categories</p>
          <TaxonomyList kind="categories" items={categories} onChanged={onChanged} headers={headers} isDark={isDark} />
          <p className="text-[11px] text-muted-foreground mt-1.5">Renaming a category updates every item in it. Deleting moves its items to "Uncategorized".</p>
        </div>
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Tags</p>
          <TaxonomyList kind="tags" items={tags} onChanged={onChanged} headers={headers} isDark={isDark} />
          <p className="text-[11px] text-muted-foreground mt-1.5">Tags can be attached to items for quick filtering. Deleting a tag removes it from all items.</p>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export default CategoryTagManager;
